"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const integrationPoints = [
    {
        title: "Pre-Shipment Inspection",
        body: "Joint PSI with your nominated inspector (SGS, BV, Intertek). Results released within 5 days of inspection."
    },
    {
        title: "Umpire Assay Protocol",
        body: "If CoA is disputed, a mutually agreed umpire assay house resolves within 10 business days. Cost split 50/50."
    },
    {
        title: "Material Safety Data Sheet",
        body: "Full MSDS provided per REACH and GHS standards. Dangerous Goods Declaration included for all shipments."
    },
    {
        title: "Customs Clearance Support",
        body: "Our freight partners handle import customs at Rotterdam, Antwerp, and Durban. HS Code 2812.90 standard."
    },
    {
        title: "Waste & Packaging Returns",
        body: "Empty drum collection available for EU customers under Extended Producer Responsibility schemes."
    },
    {
        title: "Technical Liaison",
        body: "Our metallurgical team is available for technical queries on alloy specification, charging practice, and yield optimisation."
    }];


export default function RefineryIntegrationSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "120px 0" }}
            aria-labelledby="refinery-integration-heading">

            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1250px", margin: "0 auto" }}>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
                    {/* Image */}
                    <div className="reveal img-offset-wrapper h-[400px]">
                        <div
                            className="overflow-hidden"
                            style={{ borderRadius: "4px", aspectRatio: "4/3" }}>

                            <AppImage
                                src="https://images.unsplash.com/photo-1628697114097-f36cc91ba655"
                                alt="Refinery interior showing smelting furnace and metallurgical processing equipment"
                                fill
                                className="object-cover" />

                        </div>
                    </div>

                    {/* Text */}
                    <div className="flex flex-col gap-8">
                        <div className="reveal">
                            <span className="type-subheading">Refinery Integration</span>
                        </div>
                        <h2
                            id="refinery-integration-heading"
                            className="type-display-dark reveal reveal-delay-1">

                            Designed to slot into
                            <br />
                            your existing process.
                        </h2>
                        <p className="type-body-lg reveal reveal-delay-2" style={{ color: "rgba(255,255,255,0.38)" }}>
                            We understand that changing feed supplier is operationally
                            disruptive. Our onboarding process is structured to minimise
                            that disruption — from technical qualification to first
                            production run.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 reveal reveal-delay-3">
                            {integrationPoints?.map((pt) =>
                                <div
                                    key={pt?.title}
                                    style={{
                                        background: "var(--color-navy-mid)",
                                        border: "1px solid var(--color-navy-light)",
                                        borderRadius: "4px",
                                        padding: "20px",
                                    }}>

                                    <p
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            color: "var(--color-white)",
                                            marginBottom: "6px"
                                        }}>

                                        {pt?.title}
                                    </p>
                                    <p className="type-body" style={{ fontSize: "0.8125rem", color: "rgba(255,255,255,0.38)" }}>
                                        {pt?.body}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}